import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";

import { metricsApi, type MetricsHourly } from "@/api/metrics";
import { optimizerApi, type HourDetail, type HourScenario } from "@/api/optimizer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CostCurveWidget } from "@/components/CostCurveWidget";
import { fmt, hourFromTs, num, todayIso } from "@/lib/format";
import { cn } from "@/lib/utils";

type Mode = "plan" | "fact" | "delta";

// utilities винесено в @/lib/format

export function HomePage2() {
  const { t } = useTranslation();
  const [date, setDate] = useState(todayIso());
  const [mode, setMode] = useState<Mode>("plan");

  const metrics = useQuery({
    queryKey: ["metrics-hourly", date],
    queryFn: () => metricsApi.hourly(date),
  });

  const rows = metrics.data ?? [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("home2.title")}</CardTitle>
          <CardDescription>{t("home2.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <Label>{t("plan.date")}</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-[180px]"
            />
          </div>
          <div className="space-y-1">
            <Label>&nbsp;</Label>
            <div className="flex gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
              {(["plan", "fact", "delta"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm transition-colors",
                    mode === m ? "bg-white shadow-sm font-medium" : "text-slate-600 hover:text-slate-900",
                  )}
                >
                  {t(`home2.view_${m}`)}
                </button>
              ))}
            </div>
          </div>
          <div className="text-xs text-slate-500 italic ml-auto">{t("home2.expand_hint")}</div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {metrics.isLoading ? (
            <div className="p-6 text-sm text-slate-500">{t("common.loading")}</div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-sm text-slate-500">{t("home1.no_data")}</div>
          ) : (
            <HoursTable date={date} rows={rows} mode={mode} />
          )}
        </CardContent>
      </Card>

      <CostCurveWidget date={date} gasOnly />
    </div>
  );
}

function HoursTable({
  date,
  rows,
  mode,
}: {
  date: string;
  rows: MetricsHourly[];
  mode: Mode;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<number | null>(null);

  const totals = useMemo(() => {
    let plan = 0;
    let fact = 0;
    for (const r of rows) {
      plan += num(r.plan_profit_uah) ?? 0;
      fact += num(r.fact_profit_uah) ?? 0;
    }
    return { plan, fact };
  }, [rows]);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50">
          <tr className="text-left text-slate-500">
            <th className="p-2 w-10" />
            <th className="p-2 w-16">{t("common.hour")}</th>
            <th className="p-2 text-right">{t("home1.power_kw")}</th>
            <th className="p-2 text-right">{t("home1.profit")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const hour = hourFromTs(r.ts);
            const isOpen = expanded === hour;
            const plan = num(mode === "delta" ? r.plan_profit_uah : mode === "plan" ? r.plan_profit_uah : null);
            const fact = num(mode === "delta" ? r.fact_profit_uah : mode === "fact" ? r.fact_profit_uah : null);
            const planP = num(r.plan_power_kw);
            const factP = num(r.fact_power_kw);
            return (
              <Fragment key={r.ts}>
                <tr
                  onClick={() => setExpanded(isOpen ? null : hour)}
                  className={cn("cursor-pointer border-t border-slate-100 hover:bg-slate-50", isOpen && "bg-slate-50")}
                >
                  <td className="p-2">{isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
                  <td className="p-2 font-mono">{String(hour).padStart(2, "0")}</td>
                  <td className="p-2 text-right font-mono">
                    {mode === "delta"
                      ? renderDelta(planP, factP)
                      : mode === "plan"
                        ? fmt(planP)
                        : fmt(factP)}
                  </td>
                  <td className="p-2 text-right font-mono">
                    {mode === "delta"
                      ? renderDelta(plan, fact)
                      : mode === "plan"
                        ? fmt(plan)
                        : fmt(fact)}
                  </td>
                </tr>
                {isOpen && (
                  <tr className="bg-slate-50/40">
                    <td colSpan={4} className="p-3">
                      <HourDetailCard date={date} hour={hour} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot className="bg-slate-50 font-medium">
          <tr>
            <td className="p-2" />
            <td className="p-2">{t("common.total")}</td>
            <td className="p-2" />
            <td className="p-2 text-right font-mono">
              {mode === "delta"
                ? renderDelta(totals.plan, totals.fact)
                : mode === "plan"
                  ? fmt(totals.plan)
                  : fmt(totals.fact)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function renderDelta(plan: number | null, fact: number | null) {
  if (plan === null || fact === null) return "—";
  const d = fact - plan;
  return (
    <span className={cn(d > 0 ? "text-emerald-700" : d < 0 ? "text-red-700" : "")}>
      {(d > 0 ? "+" : "") + fmt(d)}
    </span>
  );
}

function HourDetailCard({ date, hour }: { date: string; hour: number }) {
  const { t } = useTranslation();
  const detail = useQuery<HourDetail>({
    queryKey: ["optimizer-hour", date, hour],
    queryFn: () => optimizerApi.hour(date, hour),
  });

  if (detail.isLoading) {
    return <div className="text-sm text-slate-500">{t("common.loading")}</div>;
  }
  if (detail.isError || !detail.data) {
    return <div className="text-sm text-slate-500">{t("home1.no_data")}</div>;
  }

  const scenarios = detail.data.scenarios;
  const best = scenarios[detail.data.best_index];

  // Pick 50/75/100 by closest position in the array (index proportional).
  const pick = (frac: number): HourScenario | null => {
    if (scenarios.length === 0) return null;
    const idx = Math.round((scenarios.length - 1) * frac);
    return scenarios[idx];
  };
  const s50 = pick(0);
  const s75 = pick(0.5);
  const s100 = pick(1);

  // % рахуємо від максимуму (остання точка діапазону): при power_min=601 → 40/70/100%.
  const maxPower = s100?.power_kw ?? 0;
  const pctLabel = (s: HourScenario | null) =>
    s && maxPower > 0 ? `${Math.round((s.power_kw / maxPower) * 100)}%` : "—";

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
      <ScenarioCard label={pctLabel(s50)} s={s50} />
      <ScenarioCard label={pctLabel(s75)} s={s75} />
      <ScenarioCard label={pctLabel(s100)} s={s100} />
      <ScenarioCard label={t("home2.recommendation")} s={best} highlight />
    </div>
  );
}

function ScenarioCard({
  label,
  s,
  highlight,
}: {
  label: string;
  s: HourScenario | null;
  highlight?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        "rounded-lg border p-3 text-sm space-y-1",
        highlight ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white",
      )}
    >
      <div className="text-xs text-slate-500">{label}</div>
      {s === null ? (
        <div className="text-slate-500">—</div>
      ) : (
        <>
          <div className="font-mono text-base">
            {fmt(s.power_kw)} {t("common.kw")}
          </div>
          <div className="text-xs text-slate-500">
            {t("home1.profit")}: <span className="font-mono">{fmt(s.profit_uah)}</span>
          </div>
          <div className="text-xs text-slate-500">
            {t("cost_curve.unit")}: <span className="font-mono">{fmt(s.cost_uah_per_mwh)}</span>
          </div>
        </>
      )}
    </div>
  );
}
